/**
 * «Resumen del mes» on «Hoy» in «Análisis» (the owner, 2026-10-13, of what the page could add: «a
 * monthly summary by WhatsApp or PDF»): the month's sales and what they left, against the same
 * days of the month before; what they left, the expenses and what was left, as the books have them
 * (`estadoResultados`); what left the most; and, for the month under way, where it is going, what
 * is owed the shop and the money there is. Thirty-two characters wide, as the day's summary is
 * (`textoDelDia`), so the same text goes by WhatsApp, prints on the roll or as a PDF, and is what
 * the sheet shows.
 */

import type { Centavos } from '@abastece/shared';
import { cambioBp, type ResumenVentas } from './analisis.js';
import { ANCHO, fila, type Traducir } from './dia.js';
import { envolver } from './recibo.js';

/** How many of what left the most the summary names. */
export const MEJORES_DEL_MES = 3;

export interface DatosDelMes {
  negocio: string;
  /** The month, said: «octubre de 2026». */
  mes: string;
  /** Its days read, said: «1 – 13 oct». */
  rango: string;
  /** The month before, said short, for the comparison: «sept». */
  mesAntes: string;
  ventas: ResumenVentas;
  /** The same days of the month before. */
  antes: ResumenVentas;
  /**
   * The books' gross profit and its margin, their expenses and what was left after them: the three
   * lines add up. The gross profit was the sales', which leaves out what a delivery, a till over or
   * the exchange brought in, and the cost of what sold before it arrived: «Utilidad bruta» less
   * «Gastos» was not «Lo que quedó» (2026-10-20).
   */
  bruta: { utilidad: Centavos; margenBp: number | null };
  gastos: Centavos;
  quedo: Centavos;
  /** What left the most, the most first. */
  mejores: readonly { nombre: string; utilidad: Centavos }[];
  /** Only for the month under way: where its sales close at this pace, what is owed the shop now, the money there is now. */
  hoy: { proyectado: Centavos | null; fiado: Centavos; dinero: Centavos } | null;
}

/** «+12.3%», «-4.0%», or nothing to compare with. */
function cambio(bp: number | null, pct: (bp: number | null) => string): string {
  if (bp === null) return '—';
  return `${bp > 0 ? '+' : ''}${pct(bp)}`;
}

export function textoDelMes(d: DatosDelMes, t: Traducir, dinero: (c: number) => string, pct: (bp: number | null) => string): string {
  const lineas: string[] = [
    ...envolver(d.negocio),
    ...envolver(t('analisis.resumen.de', { mes: d.mes })),
    ...envolver(d.rango),
    '='.repeat(ANCHO),
  ];
  lineas.push(fila(t('analisis.vendido'), dinero(d.ventas.ingreso)));
  lineas.push(fila(` ${t('analisis.resumen.contra', { mes: d.mesAntes })}`, cambio(cambioBp(d.ventas.ingreso, d.antes.ingreso), pct)));
  lineas.push(fila(` ${t('analisis.numVentas')}`, String(d.ventas.ventas)));
  lineas.push(fila(t('analisis.utilidad'), dinero(d.bruta.utilidad)));
  lineas.push(fila(` ${t('analisis.resumen.margen')}`, pct(d.bruta.margenBp)));
  lineas.push(fila(t('analisis.gastosTotal'), dinero(d.gastos)));
  lineas.push(fila(t('analisis.utilidadNeta'), dinero(d.quedo)));
  if (d.mejores.length > 0) {
    lineas.push('-'.repeat(ANCHO), ...envolver(t('analisis.masDeja')));
    for (const m of d.mejores.slice(0, MEJORES_DEL_MES)) lineas.push(fila(` ${m.nombre}`, dinero(m.utilidad)));
  }
  if (d.hoy) {
    lineas.push('-'.repeat(ANCHO));
    if (d.hoy.proyectado !== null) lineas.push(fila(t('analisis.resumen.cierra'), dinero(d.hoy.proyectado)));
    lineas.push(fila(t('analisis.resumen.fiado'), dinero(d.hoy.fiado)));
    lineas.push(fila(t('analisis.resumen.dinero'), dinero(d.hoy.dinero)));
  }
  return lineas.join('\n');
}
